/**
 * Lead filter state for the batch pipeline
 */
export interface LeadFilterState {
  // Tier filter (multi-select)
  tiers: string[];  // ["Tier1", "Tier2"]

  // ZIP codes (multi-select)
  zipCodes: string[];

  // Units range
  unitMin: number | null;
  unitMax: number | null;

  // Score range (0-100)
  scoreMin: number | null;
  scoreMax: number | null;

  // Rental license status
  licenseStatus: 'licensed' | 'unlicensed' | 'any';

  // Market status
  marketStatus: 'on-market' | 'off-market' | 'any';

  // Date range
  dateFrom: string | null;  // ISO date string
  dateTo: string | null;

  // Sorting
  sortBy: string;
  sortOrder: 'asc' | 'desc';

  // Pagination
  page: number;
  limit: number;
}

export const DEFAULT_LEAD_FILTERS: LeadFilterState = {
  tiers: [],
  zipCodes: [],
  unitMin: null,
  unitMax: null,
  scoreMin: null,
  scoreMax: null,
  licenseStatus: 'any',
  marketStatus: 'any',
  dateFrom: null,
  dateTo: null,
  sortBy: 'cap_rate',
  sortOrder: 'desc',
  page: 1,
  limit: 50,
};

/**
 * Saved search preset
 */
export interface SavedSearchPreset {
  id: string;
  name: string;
  filters: Partial<LeadFilterState>;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Filter count from API
 */
export interface FilterCount {
  value: string;
  count: number;
  label?: string;
}

/**
 * API response for filter counts
 */
export interface FilterCounts {
  tier_counts: FilterCount[];
  license_counts: FilterCount[];
  market_counts: FilterCount[];
  totalMatching?: number;
}

/**
 * Screen status for lead processing
 */
export type ScreenStatus = 'pending' | 'screened' | 'failed';

/**
 * Lead result from API
 */
export interface LeadResult {
  id: string;
  address: string;
  formatted_address: string | null;
  neighborhood: string | null;
  neighborhood_tier: string;
  zip_code: string | null;
  total_units: number;
  market_value: number | null;
  cap_rate: number | null;
  lead_score: number | null;
  noi: number | null;
  year_built: number | null;
  has_rental_license: boolean | null;
  rental_license_status: string | null;
  screen_status: ScreenStatus;
  thumbnail_url?: string | null;
}

/**
 * API response for lead results
 */
export interface LeadResultsResponse {
  total: number;
  page: number;
  limit: number;
  pages: number;
  leads: LeadResult[];
  filter_counts: FilterCounts;
}

/**
 * URL parameter mapping for lead filters
 */
export const URL_PARAM_MAP = {
  tiers: 't',
  zipCodes: 'z',
  unitMin: 'umin',
  unitMax: 'umax',
  scoreMin: 'smin',
  scoreMax: 'smax',
  licenseStatus: 'lic',
  marketStatus: 'mkt',
  dateFrom: 'from',
  dateTo: 'to',
  sortBy: 'sort',
  sortOrder: 'order',
  page: 'p',
  limit: 'l',
} as const;

/**
 * Reverse URL parameter mapping
 */
export const REVERSE_URL_PARAM_MAP = Object.fromEntries(
  Object.entries(URL_PARAM_MAP).map(([key, value]) => [value, key])
) as Record<string, keyof typeof URL_PARAM_MAP>;

/**
 * Sort by options for leads
 */
export type LeadSortBy = 'cap_rate' | 'lead_score' | 'total_units' | 'market_value' | 'year_built' | 'address';

/**
 * Sort order options
 */
export type SortOrder = 'asc' | 'desc';

/**
 * License status filter options
 */
export type LicenseStatus = 'any' | 'licensed' | 'unlicensed';

/**
 * Market status filter options
 */
export type MarketStatus = 'any' | 'on-market' | 'off-market';
